import React, { createContext, useContext, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";

// Pages
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import ServiceDetailPage from "./pages/ServiceDetailPage";
import GrowthSystemsPage from "./pages/GrowthSystemsPage";
import SoftwarePage from "./pages/SoftwarePage";
import PricingPage from "./pages/PricingPage";
import CaseStudiesPage from "./pages/CaseStudiesPage";
import ResourcesPage from "./pages/ResourcesPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import ContactPage from "./pages/ContactPage";
import LeadMagnetPage from "./pages/LeadMagnetPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import CookiesPage from "./pages/CookiesPage";
import ClientOnboardingPage from "./pages/ClientOnboardingPage";
import ChatGPTAdsPage from "./pages/ChatGPTAdsPage";

// Client Reports (standalone, no header/footer)
import SLDJulyReport from "./pages/reports/SLDJulyReport";

// Portal
import { AuthProvider, useAuth } from "./pages/portal/AuthContext";
import PortalLogin from "./pages/portal/PortalLogin";
import PortalDashboard from "./pages/portal/PortalDashboard";
import ClientDetail from "./pages/portal/ClientDetail";

// Components
import Header from "./components/Header";
import Footer from "./components/Footer";

// Context
export const CurrencyContext = createContext();

export const useCurrency = () => useContext(CurrencyContext);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Currency Provider
const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    const saved = localStorage.getItem("optimagrowth-currency");
    return saved || "ZAR";
  });

  const currencies = {
    ZAR: { symbol: "R", name: "South African Rand", rate: 1 },
    GBP: { symbol: "£", name: "British Pound", rate: 0.045 },
    USD: { symbol: "$", name: "US Dollar", rate: 0.055 },
  };

  const formatPrice = (priceInZAR) => {
    const converted = Math.round(priceInZAR * currencies[currency].rate);
    return `${currencies[currency].symbol}${converted.toLocaleString()}`;
  };

  useEffect(() => {
    localStorage.setItem("optimagrowth-currency", currency);
  }, [currency]);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, currencies, formatPrice }}>
      {children}
    </CurrencyContext.Provider>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Page transition wrapper
const PageWrapper = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  );
};

// Portal protected route
const PortalRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="w-8 h-8 border-4 border-optima-blue border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <PortalLogin />;
  return children;
};

function App() {
  return (
    <HelmetProvider>
    <CurrencyProvider>
      <AuthProvider>
        <BrowserRouter>
            <ScrollToTop />
            <Routes>
              {/* Portal routes — no header/footer */}
              <Route path="/portal" element={<PortalRoute><PortalDashboard /></PortalRoute>} />
              <Route path="/portal/login" element={<PortalLogin />} />
              <Route path="/portal/clients/:id" element={<PortalRoute><ClientDetail /></PortalRoute>} />

              {/* Client report routes — standalone, no header/footer */}
              <Route path="/report/source-leak-detection-july-2026" element={<SLDJulyReport />} />

              {/* Main site routes */}
              <Route path="/*" element={<MainSite />} />
            </Routes>
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </CurrencyProvider>
    </HelmetProvider>
  );
}

function MainSite() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <AnimatePresence mode="wait">
          <Routes>
            <Route path="/" element={<PageWrapper><HomePage /></PageWrapper>} />
            <Route path="/about" element={<PageWrapper><AboutPage /></PageWrapper>} />
            <Route path="/services" element={<PageWrapper><ServicesPage /></PageWrapper>} />
            <Route path="/services/:slug" element={<PageWrapper><ServiceDetailPage /></PageWrapper>} />
            <Route path="/growth-systems" element={<PageWrapper><GrowthSystemsPage /></PageWrapper>} />
            <Route path="/software" element={<PageWrapper><SoftwarePage /></PageWrapper>} />
            <Route path="/pricing" element={<PageWrapper><PricingPage /></PageWrapper>} />
            <Route path="/case-studies" element={<PageWrapper><CaseStudiesPage /></PageWrapper>} />
            <Route path="/resources" element={<PageWrapper><ResourcesPage /></PageWrapper>} />
            <Route path="/blog" element={<PageWrapper><BlogPage /></PageWrapper>} />
            <Route path="/blog/:slug" element={<PageWrapper><BlogPostPage /></PageWrapper>} />
            <Route path="/contact" element={<PageWrapper><ContactPage /></PageWrapper>} />
            <Route path="/guide/:slug" element={<PageWrapper><LeadMagnetPage /></PageWrapper>} />
            <Route path="/privacy" element={<PageWrapper><PrivacyPage /></PageWrapper>} />
            <Route path="/terms" element={<PageWrapper><TermsPage /></PageWrapper>} />
            <Route path="/cookies" element={<PageWrapper><CookiesPage /></PageWrapper>} />
            <Route path="/client-onboarding" element={<PageWrapper><ClientOnboardingPage /></PageWrapper>} />
            <Route path="/services/chatgpt-ads" element={<PageWrapper><ChatGPTAdsPage /></PageWrapper>} />
          </Routes>
        </AnimatePresence>
      </main>
      <Footer />
    </div>
  );
}

export default App;
